import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  addMatterItem, downloadMemoPdf, finalizeMemo, generateMemo, getMatter, getMemo, removeMatterItem, updateMatter, updateMemo,
  type Matter, type MatterItem, type MatterStatus, type Memo,
} from '../../api/mattersApi';
import { searchRegulations, type SearchResult } from '../../api/searchApi';

/** SRS #49/#50 — one matter: instruments in play, memo drafting, lawyer review, client-ready PDF. */

import { useWording } from './wording';

const REL: MatterItem['relevance'][] = ['relevant', 'background', 'excluded'];

/** Height for an editable section, from what is actually in it.
 *
 *  Every section used to get 70px (140 for the analysis), so the model's own
 *  output — a numbered list of recommendations runs well past that — sat
 *  inside a four-line box with its own scrollbar. The author could not read
 *  the draft they were being asked to sign off without scrolling each section
 *  separately, and a box showing the middle of a sentence reads as truncated
 *  text rather than a short box.
 *
 *  Computed rather than measured: jsdom reports no layout, so `scrollHeight`
 *  auto-growing cannot be tested, and this can. ~90 characters to a line at
 *  this width, 22px a line, capped so one runaway section cannot push the
 *  sign-off button off the screen. */
export const sectionHeight = (body: string): number => {
  const lines = (body || '').split('\n').reduce((n, l) => n + Math.max(1, Math.ceil(l.length / 90)), 0);
  return Math.min(520, Math.max(70, lines * 22 + 24));
};

export default function MatterDetailPage() {
  const { id } = useParams<{ id: string }>();
  const W = useWording();
  const matterId = Number(id);
  const navigate = useNavigate();
  const [m, setM] = useState<Matter | null>(null);
  const [memo, setMemo] = useState<Memo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [artifactInput, setArtifactInput] = useState('');
  const [found, setFound] = useState<SearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [question, setQuestion] = useState('');
  const [audience, setAudience] = useState<'client' | 'internal'>('client');
  const [edits, setEdits] = useState<Record<string, string>>({});

  const load = useCallback(async (openMemo?: number) => {
    try {
      const d = await getMatter(matterId); setM(d);
      const target = openMemo ?? memo?.id ?? d.memos?.[0]?.id;
      if (target) setMemo(await getMemo(target)); else setMemo(null);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<unknown>, after?: (r: any) => number | undefined) => {
    setBusy(true); setError(null);
    try { const r = await fn(); await load(after ? after(r) : undefined); }
    catch (e) { setError(e instanceof Error ? e.message : 'Action failed'); }
    finally { setBusy(false); }
  };
  /** Attaching used to accept nothing but a pasted UUID, so a lawyer working
   *  inside the matter had to leave it, find the instrument, copy its link and
   *  come back. Typing a name now searches the corpus; a pasted link or id is
   *  still attached directly, because the "Add to matter" button on a
   *  regulation page hands the user exactly that. */
  const uuidIn = (raw: string): string | undefined =>
    (raw.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i) || [])[0];

  const attach = (artifactId: string) =>
    run(() => addMatterItem(matterId, artifactId)).then(() => { setArtifactInput(''); setFound(null); });

  const addArtifact = async () => {
    const raw = artifactInput.trim();
    if (!raw) return;
    const uuid = uuidIn(raw);
    if (uuid) { attach(uuid); return; }
    setError(null); setSearching(true);
    try {
      // The instruments already attached are not offered again.
      const attached = new Set((m?.items || []).map(i => i.artifact_id));
      const r = await searchRegulations({ query: raw, size: 8 });
      setFound(r.results.filter(x => !attached.has(x.id)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Search failed');
      setFound([]);
    } finally { setSearching(false); }
  };
  const saveEdits = () => memo && run(() => updateMemo(memo.id, { sections: Object.entries(edits).map(([key, body]) => ({ key, body })) }), () => memo.id).then(() => setEdits({}));

  if (!m) return <div style={{ padding: 24 }}>{error ? <div role="alert" style={{ color: 'var(--ds-danger-text)' }}>{error}</div> : 'Loading…'}</div>;
  const box: React.CSSProperties = { border: '1px solid var(--ds-border)', borderRadius: 12, padding: 16, display: 'grid', gap: 10 };
  const btn = (primary = false): React.CSSProperties => ({ padding: '7px 12px', borderRadius: 8, border: primary ? 'none' : '1px solid var(--ds-border-strong)', background: primary ? 'var(--ds-brand-blue)' : 'var(--ds-surface)', color: primary ? 'var(--ds-text-on-dark)' : 'var(--ds-text)', fontWeight: primary ? 700 : 500, cursor: 'pointer', fontSize: 13 });

  return (
    <div style={{ padding: 24, display: 'grid', gap: 16, maxWidth: 1100 }}>
      <div>
        <Link to="/matters" style={{ fontSize: 13, color: 'var(--ds-text-muted)' }}>← Matters</Link>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginTop: 6 }}>
          <div>
            <h1 style={{ fontSize: 22, margin: 0 }}>{m.title}</h1>
            <div style={{ color: 'var(--ds-text-muted)', fontSize: 13.5 }}>{m.client_name}{m.reference ? ` · ref ${m.reference}` : ''}{m.lead ? ` · ${m.lead}` : ''} · {m.jurisdictions.join(', ') || 'no jurisdictions set'}</div>
          </div>
          <span style={{ flex: 1 }} />
          <select aria-label="Matter status" value={m.status} onChange={e => run(() => updateMatter(matterId, { status: e.target.value as MatterStatus }))} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--ds-border-strong)' }}>
            <option value="open">Open</option><option value="on_hold">On hold</option><option value="closed">Closed</option>
          </select>
        </div>
      </div>
      {error && <div role="alert" style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--ds-danger-bg)', color: 'var(--ds-danger-text)' }}>{error}</div>}

      <section style={box} aria-label="Instruments">
        <div style={{ fontWeight: 700 }}>Instruments on this matter</div>
        {(m.items || []).length === 0 && <div style={{ color: 'var(--ds-text-muted)', fontSize: 13.5 }}>None yet — open a regulation and choose “Add to matter”, or paste its link below.</div>}
        {(m.items || []).map(it => (
          <div key={it.artifact_id} data-testid={`item-${it.artifact_id}`} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: 13.5 }}>
            <div style={{ flex: '1 1 260px' }}>
              <Link to={`/dashboard/regulations/${it.artifact_id}`}>{it.title || it.artifact_id}</Link>
              <div style={{ color: 'var(--ds-text-muted)', fontSize: 12 }}>{it.jurisdiction || ''}{it.status ? ` · ${it.status}` : ''}{it.source_url ? <> · <a href={it.source_url} target="_blank" rel="noreferrer">source</a></> : ''}</div>
            </div>
            <select aria-label={`Relevance of ${it.title || it.artifact_id}`} value={it.relevance} onChange={e => run(() => addMatterItem(matterId, it.artifact_id, undefined, e.target.value as MatterItem['relevance']))} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--ds-border-strong)' }}>
              {REL.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <button type="button" style={btn()} onClick={() => run(() => removeMatterItem(matterId, it.artifact_id))}>Remove</button>
          </div>
        ))}
        <form style={{ display: 'flex', gap: 8 }} onSubmit={e => { e.preventDefault(); addArtifact(); }}>
          <input aria-label="Find a regulation" placeholder="Search by name, or paste a regulation link" value={artifactInput}
                 onChange={e => { setArtifactInput(e.target.value); if (found) setFound(null); }}
                 style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--ds-border-strong)' }} />
          <button type="submit" style={btn()} disabled={busy || searching || !artifactInput.trim()}>{searching ? 'Searching…' : 'Find'}</button>
        </form>
        {found !== null && (
          <div aria-label="Search results" style={{ display: 'grid', gap: 6 }}>
            {found.length === 0 && <div style={{ color: 'var(--ds-text-muted)', fontSize: 13 }}>Nothing found for that. Try fewer words, or paste the regulation&rsquo;s link.</div>}
            {found.map(r => (
              <div key={r.id} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', padding: '8px 10px', border: '1px solid var(--ds-border)', borderRadius: 8 }}>
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{r.title}</div>
                  <div style={{ color: 'var(--ds-text-muted)', fontSize: 12 }}>{r.jurisdiction || ''}{r.status ? ` · ${r.status}` : ''}</div>
                </div>
                <button type="button" style={btn(true)} disabled={busy} onClick={() => attach(r.id)}>Attach</button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={box} aria-label="Memoranda">
        <div style={{ fontWeight: 700 }}>{W.deliverables}</div>
        <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr auto auto' }}>
          <input aria-label="Question presented" placeholder={W.legal ? "Question presented, e.g. Does the EU AI Act apply to the client's hiring tool?" : "Purpose of the brief, e.g. What must the client do before the EU AI Act's high-risk obligations apply?"} value={question} onChange={e => setQuestion(e.target.value)} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--ds-border-strong)' }} />
          <select aria-label="Audience" value={audience} onChange={e => setAudience(e.target.value as 'client' | 'internal')} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--ds-border-strong)' }}>
            <option value="client">Client-facing</option><option value="internal">{W.internal}</option>
          </select>
          <button type="button" style={btn(true)} disabled={busy || question.trim().length < 10} onClick={() => run(() => generateMemo(matterId, question, audience), (r: Memo) => r.id).then(() => setQuestion(''))}>{W.legal ? 'Draft memo' : 'Draft brief'}</button>
        </div>
        {(m.memos || []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(m.memos || []).map(x => (
              <button key={x.id} type="button" aria-pressed={memo?.id === x.id} onClick={() => getMemo(x.id).then(setMemo)} style={{ ...btn(), borderColor: memo?.id === x.id ? 'var(--ds-brand-blue)' : 'var(--ds-border-strong)', background: memo?.id === x.id ? 'var(--ds-info-bg)' : 'var(--ds-surface)' }}>
                v{x.version} · {x.status === 'final' ? 'Final' : 'Draft'} · {x.audience}
              </button>
            ))}
          </div>
        )}
        {memo && (
          <div data-testid="memo" style={{ display: 'grid', gap: 12, borderTop: '1px solid var(--ds-border)', paddingTop: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 800 }}>{memo.title}</div>
                <div style={{ fontSize: 12.5, color: 'var(--ds-text-muted)' }}>
                  Version {memo.version} · {memo.status === 'final' ? `Final — signed off by ${memo.finalized_by} on ${memo.finalized_at?.slice(0, 10)}` : 'Draft — not for reliance'} · drafted by {memo.generated_by === 'llm' ? 'AI (sources fixed)' : 'template'} · {memo.audience === 'internal' ? 'privileged & confidential' : 'client-facing'}
                </div>
              </div>
              <span style={{ flex: 1 }} />
              {memo.status === 'draft' && Object.keys(edits).length > 0 && <button type="button" style={btn()} onClick={saveEdits} disabled={busy}>Save edits</button>}
              {memo.status === 'draft' && <button type="button" style={{ ...btn(true), background: 'var(--ds-success)' }} disabled={busy || Object.keys(edits).length > 0} title={Object.keys(edits).length ? 'Save edits first' : W.signoff} onClick={() => run(() => finalizeMemo(memo.id), () => memo.id)}>Finalise (client-ready)</button>}
              <button type="button" style={btn()} onClick={() => downloadMemoPdf(memo.id).catch(e => setError(e.message))}>PDF</button>
            </div>
            {memo.sections.map(s => (
              <div key={s.key}>
                <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 4 }}>{s.heading}</div>
                {memo.status === 'draft'
                  ? <textarea aria-label={s.heading} value={edits[s.key] ?? s.body} onChange={e => setEdits({ ...edits, [s.key]: e.target.value })} style={{ width: '100%', minHeight: sectionHeight(edits[s.key] ?? s.body), padding: 8, borderRadius: 8, border: '1px solid var(--ds-border-strong)', fontSize: 13.5, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  : <div style={{ whiteSpace: 'pre-wrap', fontSize: 13.5, lineHeight: 1.55 }}>{s.body}</div>}
              </div>
            ))}
            {memo.citations.length > 0 && (
              <div>
                <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 4 }}>Sources</div>
                <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                  {memo.citations.map(c => (
                    <li key={c.n} style={{ marginBottom: 4 }}>
                      {c.title} — {c.jurisdiction}{c.status ? ` (${c.status})` : ''}{c.source_url ? <> · <a href={c.source_url} target="_blank" rel="noreferrer">source</a></> : ' · no source URL published'}
                      <span style={{ color: 'var(--ds-text-muted)' }}>{c.retrieved_at ? ` · retrieved ${c.retrieved_at}` : ''}{c.verified_at ? ` · verified ${c.verified_at}` : ''}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </section>
      <button type="button" onClick={() => navigate('/matters')} style={{ ...btn(), justifySelf: 'start' }}>Back to matters</button>
    </div>
  );
}
